import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const WestminsterHomesForSalePage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Westminster&county=Orange&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching Westminster properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Westminster Homes for Sale | Find Your Dream Home | Truc Doan</title>
        <meta name="description" content="Browse Westminster CA homes for sale. Explore neighborhoods, schools, parks, and Vietnamese community amenities. Truc Doan, your local Westminster realtor." />
        <meta name="keywords" content="Westminster homes for sale, Westminster CA real estate, Westminster realtor, Westminster houses, buy home Westminster CA, Westminster neighborhoods, Little Saigon homes, Vietnamese community Westminster, Truc Doan Westminster" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/westminster-homes-for-sale`} />
        <meta property="og:title" content="Westminster Homes for Sale | Find Your Dream Home | Truc Doan" />
        <meta property="og:description" content="Find your dream home in Westminster CA. Browse current listings, explore neighborhoods, and work with local expert Truc Doan." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/westminster-homes-for-sale`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Westminster, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/westminster-homes-for-sale`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan is a trusted Westminster CA real estate agent specializing in helping buyers and sellers in Westminster, Little Saigon, and surrounding Orange County communities.",
            "areaServed": {
              "@type": "City",
              "name": "Westminster, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Westminster Homes for Sale</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Discover your perfect home in Westminster, California — the heart of Orange County's Vietnamese-American community.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Why Buy a Home in Westminster, CA?</h2>
          <p>Westminster is one of Orange County's most vibrant and culturally rich cities. Home to Little Saigon — the largest Vietnamese-American community in the United States — Westminster offers a unique blend of cultural heritage, excellent dining, bustling shopping districts, and family-friendly neighborhoods. With top-rated schools, beautiful parks, and easy access to freeways and beaches, Westminster is an ideal place to call home.</p>
          <p>Whether you're a first-time homebuyer looking for an affordable entry into Orange County real estate or a growing family seeking a welcoming community with diverse amenities, Westminster delivers exceptional value. The city's central location provides quick access to employment centers in Irvine, Costa Mesa, and Long Beach while maintaining a strong sense of neighborhood community.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Westminster Neighborhoods & Amenities</h2>
          <p>Westminster features diverse neighborhoods with distinct character, from established single-family home communities to newer developments with modern amenities.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Little Saigon District</h3>
              <p>The cultural and commercial heart of Westminster, featuring hundreds of Vietnamese restaurants, shops, and cultural centers along Bolsa Avenue.</p>
            </div>
            <div className="amenity">
              <h3>Top-Rated Schools</h3>
              <p>Westminster School District and Huntington Beach Union High School District serve families with highly rated elementary, middle, and high schools.</p>
            </div>
            <div className="amenity">
              <h3>Parks & Recreation</h3>
              <p>Enjoy Sigler Park, Liberty Park, and Westminster Memorial Park. The city offers sports leagues, community events, and family activities year-round.</p>
            </div>
            <div className="amenity">
              <h3>Asian Garden Mall</h3>
              <p>Phước Lộc Thọ (Asian Garden Mall) is the largest Vietnamese-American shopping center, offering authentic cuisine, jewelry, and cultural experiences.</p>
            </div>
            <div className="amenity">
              <h3>Dining & Cuisine</h3>
              <p>World-class Vietnamese phở, bánh mì, and seafood restaurants. Westminster is a food lover's paradise with options for every taste and budget.</p>
            </div>
            <div className="amenity">
              <h3>Central Location</h3>
              <p>Minutes from the 405, 22, and 39 freeways. Easy access to Huntington Beach, Costa Mesa, Garden Grove, and major employment centers.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Westminster Real Estate Market Snapshot</h2>
          <p>The Westminster housing market remains competitive with strong demand from buyers seeking value in Orange County. Here's a current overview of market conditions:</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Median Home Price (Westminster)</td>
                <td>~$750,000</td>
              </tr>
              <tr>
                <td>Average Days on Market</td>
                <td>20–30 Days</td>
              </tr>
              <tr>
                <td>Most Popular Price Range</td>
                <td>$550K – $900K</td>
              </tr>
              <tr>
                <td>Property Types</td>
                <td>Single Family, Condos, Townhomes</td>
              </tr>
              <tr>
                <td>School Districts</td>
                <td>Westminster SD, HB Union HSD</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Current Homes for Sale in Westminster</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">
                    {formatPropertyAddress(property)}, CA {property.postal_code}
                  </div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>
              Browse all available Westminster listings by clicking "Search Listings" below.
            </p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Buy or Sell in Westminster?</h2>
          <p>Contact Truc Doan today for a free consultation. Whether you're buying your first home or selling your current property, I'll guide you every step of the way.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Westminster Real Estate Inquiry" className="cta-button primary">
              Email Truc Doan
            </a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">
              Call +1-818-818-0989
            </a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>
              Search Westminster Listings
            </button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default WestminsterHomesForSalePage;
